// The reading window is derived from `started_at`; `started_at` stays the working start.
import { convertMs } from '@/lib/commonHelpers';
import type { ExamHeaderTimerConfig } from '@/pages/experiences/show/take/header/types';
import { pausedTimeMs } from '@/pages/experiences/show/take/header/useExamCountdown';
import { hasPauseStatus } from '@/pages/experiences/utils/realtimeExperienceHelper';

export const RUNNING_VIEWS = ['startTest', 'endTest', 'resumeTest'];

export interface ReadingTimePhase {
  minutes: number;
  startedReadingAt: number;
  endsAt: number;
  isReading: boolean;
  isPaused: boolean;
}

export const readingTimeMinutes = (settings: any, enabled: boolean): number => {
  if (!enabled || !settings?.is_timed || !settings?.allow_reading_time) return 0;
  if (settings?.mode === 'paper') return 0;
  const minutes = Number(settings?.reading_time);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
};

export interface ResolveReadingTimePhaseArgs {
  settings: any;
  enabled: boolean;
  userInfo: any;
  experienceInfo?: any;
  now: number;
  pauseEnabled?: boolean;
}

export const resolveReadingTimePhase = ({
  settings,
  enabled,
  userInfo,
  experienceInfo,
  now,
  pauseEnabled = false,
}: ResolveReadingTimePhaseArgs): ReadingTimePhase => {
  const minutes = readingTimeMinutes(settings, enabled);
  const startedAt = userInfo?.started_at ?? null;
  const startedReadingAt = userInfo?.started_reading_at ?? (startedAt ?? 0) - convertMs(minutes);
  const isPaused =
    !!experienceInfo?.paused ||
    hasPauseStatus(userInfo?.pause_status ?? null, 'paused') ||
    hasPauseStatus(userInfo?.pause_status ?? null, 'unpause_pending');
  const pauseOffset = pauseEnabled
    ? (userInfo?.unpaused_offset ?? 0) +
      pausedTimeMs({
        startedAt,
        studentPauseStatus: userInfo?.pause_status,
        studentPausedAt: userInfo?.paused_at,
        experiencePaused: !!experienceInfo?.paused,
        experiencePausedAt: experienceInfo?.paused_at,
        now,
      })
    : 0;
  const endsAt = (startedAt ?? 0) + pauseOffset;
  const isReading =
    minutes > 0 &&
    startedAt != null &&
    RUNNING_VIEWS.includes(userInfo?.currentView ?? '') &&
    now < endsAt;
  return { minutes, startedReadingAt, endsAt, isReading, isPaused };
};

export const pauseTimerConfig = (userInfo: any, experienceInfo: any, pauseEnabled: boolean) => ({
  unpausedOffset: userInfo?.unpaused_offset ?? undefined,
  pauseEnabled,
  studentPauseStatus: userInfo?.pause_status ?? null,
  studentPausedAt: userInfo?.paused_at ?? undefined,
  experiencePaused: !!experienceInfo?.paused,
  experiencePausedAt: experienceInfo?.paused_at ?? undefined,
});

export const readingTimerConfig = (
  phase: ReadingTimePhase,
  working: ExamHeaderTimerConfig
): ExamHeaderTimerConfig => ({
  phase: 'reading',
  startedAt: phase.startedReadingAt,
  duration: phase.minutes,
  extraTime: 0,
  extraTimeOffset: 0,
  unpausedOffset: working.unpausedOffset,
  pauseEnabled: working.pauseEnabled,
  studentPauseStatus: working.studentPauseStatus,
  studentPausedAt: working.studentPausedAt,
  experiencePaused: working.experiencePaused,
  experiencePausedAt: working.experiencePausedAt,
  canToggleTimerMode: false,
});
